import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { compararValoresOrdenacao } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { UtilService } from '../../../../shared/services/util.service';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';

import { CategoriaFinanceiraService } from '../../../../shared/services/apis/financas/categoria-financeira.service';
import { CategoriaFinanceiraStruct } from '../../../../shared/services/structs/financas/categoria-financeira.struct';
import { DeletarCategoriaFinanceiraRequest } from '../../../../shared/services/requests/financas/deletar-categoria-financeira.request';

import { ReturnStruct } from '../../../../shared/services/structs/return.struct';
import { ExtracaoDadosData, ExtracaoDadosModalComponent, FiltroExtracao } from '../../../../shared/components/extracao-dados-modal/extracao-dados-modal.component';
import { OrigemPedidoExportacaoEnum } from '../../../../shared/enums/batch-extracao-dados/origem-pedido-exportacao.enum';
import { TipoExtracaoEnum } from '../../../../shared/enums/batch-extracao-dados/tipo-extracao.enum';
import { DialogImportarCategoriasFinanceirasComponent } from './dialogs/importar-categorias-financeiras/dialog-importar-categorias-financeiras.component';
import { DialogMoverCategoriasFinanceirasComponent, MoverCategoriasDialogData } from './dialogs/mover-categorias-financeiras/dialog-mover-categorias-financeiras.component';
import { MigracaoCategoriaModalComponent } from '../config-financas-shared/migracao-categoria-modal/migracao-categoria-modal.component';

export interface CategoriaDisplayRow {
    data: CategoriaFinanceiraStruct;
    isVariation: boolean;
    level: number;
}

@Component({
    selector: 'app-categoria-financeira-lista',
    standalone: true,
    imports: [
        CommonModule,
        HeaderComponent,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatCheckboxModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatBadgeModule,
        MatTooltipModule,
        MatSelectModule,
        EmptyListComponent,
        RouterModule,
        MatSortModule,
        LoadingBoxDefaultListComponent
    ],
    templateUrl: './categoria-financeira-lista.component.html',
    styleUrls: ['./categoria-financeira-lista.component.css'],
})
export class CategoriaFinanceiraListaComponent implements OnInit, OnDestroy {

    public moduloEnum = ModuloEnum.Configuracoes;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

    public model: FormGroup;
    public _isLoading: boolean = false;
    public _isLoadingAction: boolean = false;

    // Data and Pagination
    public _dataSource = new MatTableDataSource<CategoriaFinanceiraStruct>();
    public _flatDataSource = new MatTableDataSource<CategoriaDisplayRow>();
    public _paginaAtual: number = 0;
    public _tamanhoPagina: number = 20;
    public _ultimaPagina: boolean = false;
    public expandedElements = new Set<CategoriaFinanceiraStruct>();
    public _totalRegistros: number = 0;

    public displayedColumns: string[] = [
        'select',
        'descricao',
        'tipoCategoriaFinanceira',
        'tipoCategoriaDre',
        'isAtivo',
        'actions'
    ];

    public selection = new SelectionModel<CategoriaFinanceiraStruct>(true, []);

    private sortDirection: 'asc' | 'desc' | '' = '';

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        if (!sort) return;
        sort.sortChange.subscribe(() => {
            this.sortDirection = sort.direction;
            this.aplicarOrdenacaoHierarquica();
        });
    }

    constructor(
        private formBuilder: FormBuilder,
        private categoriaFinanceiraService: CategoriaFinanceiraService,
        private alertService: AlertService,
        private dialog: MatDialog,
        private utilService: UtilService
    ) {
    }

    ngOnInit(): void {
        this.model = this.formBuilder.group({
            pesquisa: [''],
        });

        this.loadFilters();
        this.pesquisar();
    }

    ngOnDestroy(): void {
        this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, ['/configuracoes/financas/categorias/cadastrar']);
    }

    private loadFilters(): void {
        const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
        if (filtroSalvo) {
            this.model.patchValue(filtroSalvo);
        }
    }

    // --- Search logic ---

    public pesquisar(reset: boolean = false): void {
        if (this._isLoading && !reset) return;

        this._isLoading = true;
        const descricao = this.model.get('pesquisa')?.value || '';

        this.categoriaFinanceiraService.getAll(descricao, this._paginaAtual, this._tamanhoPagina).subscribe({
            next: (response) => {
                this._isLoading = false;
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription, AlertType.error);
                    return;
                }

                if (!response.listaCategorias || response.listaCategorias.length === 0) {
                    this._ultimaPagina = true;
                } else {
                    this._ultimaPagina = response.listaCategorias.length < this._tamanhoPagina;
                }

                this._totalRegistros = response.totalRegistros;

                // O backend já retorna os dados hierarquizados (pais com subcategorias embutidas)
                if (reset) {
                    this._dataSource.data = response.listaCategorias;
                } else {
                    this._dataSource.data = [...this._dataSource.data, ...response.listaCategorias];
                }

                if (descricao) {
                    // Com um termo de busca ativo, expande automaticamente todo o resultado
                    // (o backend já retorna só os ramos relevantes, incluindo ancestrais do match).
                    this.expandirTodosComSubcategorias(response.listaCategorias);
                }

                this.reconstruirFlatDataSource();
            },
            error: (err) => {
                this._isLoading = false;
                this.alertService.show('Erro inesperado', err.message || err, AlertType.error);
            }
        });
    }

    public clickPesquisar(): void {
        this._paginaAtual = 0;
        this._dataSource.data = [];
        this._flatDataSource.data = [];
        this._ultimaPagina = false;
        this.expandedElements.clear();
        this.selection.clear();
        this.pesquisar(true);
    }

    public exportarCategorias(): void {
        if (this._isLoadingAction) return;

        const formValue = this.model.getRawValue();
        const filtrosMapeados: FiltroExtracao[] = [];

        if (formValue.pesquisa) {
            filtrosMapeados.push({ label: 'Pesquisa', formControlName: 'searchText', valor: formValue.pesquisa });
        }

        const dialogData: ExtracaoDadosData = {
            titulo: 'Exportar Categorias',
            tipoExtracao: TipoExtracaoEnum.CategoriaFinanceiraCSV,
            origemPedidoExportacao: OrigemPedidoExportacaoEnum.ConfiguracoesFinancas,
            filtros: filtrosMapeados,
            possuiAutomacao: false,
        };

        this.dialog.open(ExtracaoDadosModalComponent, {
            data: dialogData,
            panelClass: 'extracao-dados-panel',
            disableClose: true
        });
    }

    public importarCategorias(): void {
        const dialogRef = this.dialog.open(DialogImportarCategoriasFinanceirasComponent, {
            disableClose: true,
            width: '95vw',
            maxWidth: '1200px'
        });

        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this.clickPesquisar();
            }
        });
    }

    public proximaPagina(): void {
        this._paginaAtual++;
        this.pesquisar();
    }



    // --- Selection Logic ---

    private getAllDescendants(item: CategoriaFinanceiraStruct): CategoriaFinanceiraStruct[] {
        const result: CategoriaFinanceiraStruct[] = [];
        if (item.subcategorias?.length) {
            for (const child of item.subcategorias) {
                result.push(child);
                result.push(...this.getAllDescendants(child));
            }
        }
        return result;
    }

    isAllSelected() {
        if (this._dataSource.data.length === 0) return false;
        const numSelected = this.selection.selected.length;
        let totalItems = 0;
        for (const cat of this._dataSource.data) {
            totalItems++;
            totalItems += this.getAllDescendants(cat).length;
        }
        return numSelected === totalItems && totalItems > 0;
    }

    toggleAllRows() {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        const allItems: CategoriaFinanceiraStruct[] = [];
        for (const cat of this._dataSource.data) {
            allItems.push(cat);
            allItems.push(...this.getAllDescendants(cat));
        }
        this.selection.select(...allItems);
    }

    toggleSelection(item: CategoriaFinanceiraStruct): void {
        this.selection.toggle(item);
        const descendants = this.getAllDescendants(item);
        if (descendants.length > 0) {
            if (this.selection.isSelected(item)) {
                this.selection.select(...descendants);
            } else {
                this.selection.deselect(...descendants);
            }
        }
    }

    checkboxLabel(row?: CategoriaFinanceiraStruct): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.categoriaFinanceira.idCategoriaFinanceira}`;
    }

    private expandirTodosComSubcategorias(items: CategoriaFinanceiraStruct[]): void {
        for (const item of items) {
            if (item.subcategorias?.length) {
                this.expandedElements.add(item);
                this.expandirTodosComSubcategorias(item.subcategorias);
            }
        }
    }

    public toggleRow(row: CategoriaDisplayRow): void {
        const element = row.data;
        if (element.subcategorias && element.subcategorias.length > 0) {
            if (this.expandedElements.has(element)) {
                this.expandedElements.delete(element);
            } else {
                this.expandedElements.add(element);
            }
            this.reconstruirFlatDataSource();
        }
    }

    public reconstruirFlatDataSource(): void {
        const flat: CategoriaDisplayRow[] = [];

        const addDescendants = (items: CategoriaFinanceiraStruct[], level: number) => {
            for (const cat of items) {
                flat.push({ data: cat, isVariation: level > 0, level });
                if (this.expandedElements.has(cat) && cat.subcategorias?.length) {
                    addDescendants(cat.subcategorias, level + 1);
                }
            }
        };

        addDescendants(this._dataSource.data, 0);
        this._flatDataSource.data = flat;
    }

    // --- Ordenação (preserva a hierarquia pai/filho) ---

    private compararDescricao(a: CategoriaFinanceiraStruct, b: CategoriaFinanceiraStruct): number {
        const cmp = compararValoresOrdenacao(
            a.categoriaFinanceira?.descricao ?? '',
            b.categoriaFinanceira?.descricao ?? ''
        );
        return this.sortDirection === 'desc' ? -cmp : cmp;
    }

    private ordenarArvore(items: CategoriaFinanceiraStruct[]): CategoriaFinanceiraStruct[] {
        const ordenados = [...items].sort((a, b) => this.compararDescricao(a, b));
        for (const item of ordenados) {
            if (item.subcategorias?.length) {
                item.subcategorias = this.ordenarArvore(item.subcategorias);
            }
        }
        return ordenados;
    }

    public aplicarOrdenacaoHierarquica(): void {
        if (!this.sortDirection) {
            // Sem ordenação ativa: recarrega para restaurar a ordem padrão do backend.
            this.clickPesquisar();
            return;
        }
        this._dataSource.data = this.ordenarArvore(this._dataSource.data);
        this.reconstruirFlatDataSource();
    }

    // --- Ações habilitadas/desabilitadas ---

    public hasSubcategorias(item: CategoriaFinanceiraStruct): boolean {
        return !!item.subcategorias?.length;
    }

    public algumSelecionadoTemSubcategorias(): boolean {
        return this.selection.selected.some(item => this.hasSubcategorias(item));
    }

    private getProfundidadeSubarvore(item: CategoriaFinanceiraStruct, atual: number = 0): number {
        if (!item.subcategorias?.length) return atual;
        return Math.max(...item.subcategorias.map(sub => this.getProfundidadeSubarvore(sub, atual + 1)));
    }

    public abrirModalMover(): void {
        const selecionados = this.selection.selected;
        if (selecionados.length === 0) return;

        const maiorProfundidadeSubarvore = Math.max(...selecionados.map(item => this.getProfundidadeSubarvore(item)));
        const tiposSelecionados = [...new Set(selecionados
            .map(x => x.categoriaFinanceira.idTipoCategoriaFinanceira)
            .filter((tipo): tipo is number => tipo != null))];

        const data: MoverCategoriasDialogData = {
            idsCategorias: selecionados.map(x => x.categoriaFinanceira.idCategoriaFinanceira),
            nomeCategorias: selecionados.map(x => x.categoriaFinanceira.descricao),
            maiorProfundidadeSubarvore,
            tiposSelecionados
        };

        const dialogRef = this.dialog.open(DialogMoverCategoriasFinanceirasComponent, {
            data,
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this.selection.clear();
                this.clickPesquisar();
            }
        });
    }

    public abrirModalExcluir(item?: CategoriaFinanceiraStruct): void {
        const listaParaExcluir = item ? [item] : this.selection.selected;

        if (listaParaExcluir.length === 0) return;

        const description = 'Confirma a exclusão deste(s) registro(s)? Só será possível se não possuírem movimentações associadas ou subcategorias.';

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar exclusão',
                description: description,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar'
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirm) {
                this.executarExclusao(listaParaExcluir);
            }
        });
    }

    private executarExclusao(listaParaExcluir: any[], idCategoriaMigracao?: number): void {
        this._isLoadingAction = true;

        const idsToDelete = listaParaExcluir.map(item => item.categoriaFinanceira.idCategoriaFinanceira);

        const request = new DeletarCategoriaFinanceiraRequest();
        request.idsCategorias = idsToDelete;
        if (idCategoriaMigracao) {
            request.idCategoriaMigracao = idCategoriaMigracao;
        }

        this.categoriaFinanceiraService.deletar(request).subscribe({
            next: (response: ReturnStruct) => {
                this._isLoadingAction = false;
                if (response.isError) {
                    if (response.errorCode === -999 && response.idCategoriaBloqueada) {
                        // Temos bloqueio por uso! Abrir modal de Migração
                        const itemBloqueado = listaParaExcluir.find(i => i.categoriaFinanceira.idCategoriaFinanceira === response.idCategoriaBloqueada)
                            || listaParaExcluir[0]; // fallback

                        const migracaoDialogRef = this.dialog.open(MigracaoCategoriaModalComponent, {
                            width: '500px',
                            data: {
                                idCategoriaOrigem: response.idCategoriaBloqueada,
                                idTipoCategoriaFinanceira: itemBloqueado.categoriaFinanceira.idTipoCategoriaFinanceira,
                                mensagemExibicao: response.errorDescription
                            }
                        });

                        migracaoDialogRef.afterClosed().subscribe((idDestino: number | undefined) => {
                            if (idDestino) {
                                // Refaz a exclusão com a categoria migrada atachada na Transação
                                this.executarExclusao(listaParaExcluir, idDestino);
                            }
                        });
                        return;
                    }
                    this.alertService.show('Atenção', response.errorDescription, AlertType.warning);
                    return;
                }

                this.alertService.show('Sucesso', 'Registro(s) excluído(s) com sucesso', AlertType.success);
                this.selection.clear();
                this.clickPesquisar();
            },
            error: (err) => {
                this._isLoadingAction = false;
                this.alertService.show('Erro', 'Ocorreu um erro ao excluir.', AlertType.error);
            }
        });
    }
}
